/**
 * SettlementStatement
 * ----------------------------------------------------------------------------
 * Renders the finalized round settlement for the in-app Results tab.
 * Same content as the emailed statement, structured per the approved design:
 *   1. Consolidated settlement summary (per-player cards, collect/pay lines)
 *   2. Gross scorecard (everyone in any game) — collapsible
 *   3. One block per game: net-method note, participant-scoped scorecard
 *      (collapsible), result detail (skins-won with carryover provenance /
 *      putts net winnings), and the game's dollars
 *
 * OPTION (a): the consolidated summary reuses the existing settlement math.
 * Pass in the already-built `playerSummary` array (see buildPlayerSummary below)
 * so displayed payments can never diverge from the rest of the system.
 *
 * Data contract (all already in scope in the finalized branch):
 *   games        = standings.games   // [{ game_id, game_type, display_name,
 *                                     //    bet_amount, participant_ids,
 *                                     //    results: { totals[], holes[] } }]
 *   playerSummary = result of buildPlayerSummary(standings, settlements)
 *   meta         = { courseName, dateLabel, finalizedAtLabel, teeLabel }
 *   collapsibleScorecards = true in-app, false in email-export mode
 * ----------------------------------------------------------------------------
 */

import React, { useState } from 'react';

// Best Ball and Shamble are one team game on the stroke play engine (Sept 28, 2026):
// team rows, per-player money, the blind draw — the same statement block.
const isTeamBestBall = (gt) => gt === 'best_ball' || gt === 'shamble';

/* ============================================================================
 * Settlement math — REUSED VERBATIM from the existing finalized-branch logic.
 * This is the tested money calculation; we only re-skin its output.
 * Returns a sorted array: [{ playerId, name, net, putts, skins, lines[] }]
 *   lines[] = [{ cpId, name, net, isCollect, detail }]  (collect/pay rows)
 * ==========================================================================*/
export function buildPlayerSummary(standings, settlements) {
  const allSettlements = settlements?.settlements || [];
  const playerMap = {};

  // Seed from standings totals so all players appear even if they only pay.
  // Best Ball (Sept 23, 2026): its totals are TEAMS — seed from its per-player list.
  for (const game of (standings?.games || [])) {
    const seedRows = isTeamBestBall(game.game_type) ? (game.results?.players || []) : (game.results?.totals || []);
    for (const t of seedRows) {
      if (!playerMap[t.player_id]) {
        playerMap[t.player_id] = { name: t.player_name, net: 0, putts: null, skins: null, counterparties: {}, byGameMap: {}, byGameOrder: [] };
      }
      if (game.game_type === 'putts') playerMap[t.player_id].putts = t.total_putts;
      if (game.game_type?.startsWith('skins')) {
        playerMap[t.player_id].skins = (playerMap[t.player_id].skins || 0) + (t.skins_won || 0);
      }
      // Per-game net for the by-game breakdown line (e.g. Skins -$0.50 . Putts +$2.50).
      // Short label so multiple games of a type collapse into one term.
      {
        const _pm = playerMap[t.player_id];
        const _label = game.game_type?.startsWith('skins') ? 'Skins' : (game.game_type === 'putts' ? 'Putts' : (game.display_name || 'Game'));
        if (!(_label in _pm.byGameMap)) { _pm.byGameMap[_label] = 0; _pm.byGameOrder.push(_label); }
        _pm.byGameMap[_label] += (t.net_amount || 0);
      }
    }
  }

  for (const s of allSettlements) {
    const amt = parseFloat(s.amount);
    const betAmt = s.bet_amount ? parseFloat(s.bet_amount) : 0;

    if (!playerMap[s.payer_id]) playerMap[s.payer_id] = { name: s.payer_name, net: 0, putts: null, skins: null, counterparties: {}, byGameMap: {}, byGameOrder: [] };
    if (!playerMap[s.payee_id]) playerMap[s.payee_id] = { name: s.payee_name, net: 0, putts: null, skins: null, counterparties: {}, byGameMap: {}, byGameOrder: [] };

    playerMap[s.payer_id].net -= amt;
    playerMap[s.payee_id].net += amt;

    // Carryover detection for skins_carryover (mirrors existing logic).
    let hasCarryover = false;
    if (s.game_type === 'skins_carryover') {
      const g = (standings?.games || []).find(g => g.game_id === s.game_id);
      const holes = g?.results?.holes || [];
      hasCarryover = holes.some(h => h.winner_id === s.payee_id && h.carried_over === true);
    }

    const cpLabel = s.game_type?.startsWith('skins') ? 'Skins' : ({ putts: 'Putts', stroke_play: 'Stroke Play', best_ball: 'Best Ball', shamble: 'Shamble', ctp: 'CTP', stableford: 'Stableford', sixes: 'Sixes', nassau: 'Nassau', match_play: 'Match Play', dots: 'Dots' }[s.game_type] || s.game_type || 'Game');
    // Stableford (Oct 5, 2026 — Dennis): each side's points, so every line reads
    // "Stableford · 31 pts vs 27" from the reader's side.
    let sfPts = null;
    if (s.game_type === 'stableford') {
      const g = (standings?.games || []).find(x => x.game_id === s.game_id) || (standings?.games || []).find(x => x.game_type === 'stableford');
      const tot = g?.results?.totals || [];
      const ptsOf = (id) => { const t = tot.find(x => x.player_id === id); return t && t.points != null ? t.points : null; };
      sfPts = { [s.payer_id]: ptsOf(s.payer_id), [s.payee_id]: ptsOf(s.payee_id), pot: g?.results?.payout_mode === 'pot' };
    }
    const pushGame = (mapEntry, selfId, otherId, otherName, signedAmt) => {
      if (!mapEntry.counterparties[otherId]) mapEntry.counterparties[otherId] = { name: otherName, net: 0, games: [] };
      mapEntry.counterparties[otherId].net += signedAmt;
      mapEntry.counterparties[otherId].games.push({ amount: amt, signed: signedAmt, betAmt, gameType: s.game_type, label: cpLabel, hasCarryover,
        ...(sfPts ? { myPts: sfPts[selfId], theirPts: sfPts[otherId], sfPot: sfPts.pot } : {}) });
    };
    pushGame(playerMap[s.payer_id], s.payer_id, s.payee_id, s.payee_name, -amt);
    pushGame(playerMap[s.payee_id], s.payee_id, s.payer_id, s.payer_name, +amt);
  }

  // Build display rows.
  const fmtDetail = (games) => games.map(g => {
    const bet = g.betAmt || 0;
    let unit = '';
    if (g.gameType === 'putts' && bet > 0) {
      const n = Math.round(g.amount / bet); unit = `${n} putt${n !== 1 ? 's' : ''}`;
    } else if (g.gameType === 'skins_no_carryover' && bet > 0) {
      const n = Math.round(g.amount / bet); unit = `${n} skin${n !== 1 ? 's' : ''}`;
    } else if (g.gameType === 'skins_carryover') {
      const n = bet > 0 ? Math.round(g.amount / bet) : 0;
      unit = n > 0 ? `${n} skin${n !== 1 ? 's' : ''}${g.hasCarryover ? ', incl. carryover' : ''}` : '';
    } else if (g.gameType === 'skins_pot' || g.gameType === 'skins_pot_gross') {
      unit = 'pot share';
    } else if (g.gameType === 'stroke_play' || isTeamBestBall(g.gameType)) {
      unit = 'prize share';
    } else if (g.gameType === 'ctp') {
      unit = 'closest to the pin';
    } else if (g.gameType === 'stableford') {
      unit = g.myPts != null && g.theirPts != null
        ? `Stableford${g.sfPot ? ' pot' : ''} · ${g.myPts} pts vs ${g.theirPts}`
        : 'Stableford points';
    }
    // Oct 7, 2026: with no unit (the player app's rows carry no bet amount), name the
    // game, so a line reads "Skins $1.50, Putts $0.50" instead of bare amounts.
    return unit ? `${unit} ($${g.amount.toFixed(2)})` : `${g.label} $${g.amount.toFixed(2)}`;
  }).join(', ');

  return Object.entries(playerMap)
    .sort((a, b) => b[1].net - a[1].net)
    .map(([playerId, p]) => {
      const cpEntries = Object.entries(p.counterparties)
        .map(([cpId, cp]) => ({ cpId, cp, cpNet: parseFloat(cp.net.toFixed(2)) }))
        .sort((a, b) => b.cpNet - a.cpNet);
      // Money that actually moves: the pair nets to a non-zero amount.
      const lines = cpEntries
        .filter(e => Math.abs(e.cpNet) >= 0.005)
        .map(({ cpId, cp, cpNet }) => ({ cpId, name: cp.name, net: cpNet, isCollect: cpNet > 0, detail: fmtDetail(cp.games) }));
      // Cross-game washes: a pair nets to $0 only when it had non-zero results in
      // more than one game that cancelled. A same-game tie writes no settlement row,
      // so it never reaches this list. Showing the parts explains WHY the pair is
      // square -- the exact case that read as a missing amount before.
      const evens = cpEntries
        .filter(e => Math.abs(e.cpNet) < 0.005 && (e.cp.games || []).length > 0)
        .map(({ cpId, cp }) => ({
          cpId, name: cp.name,
          parts: (cp.games || []).map(g => ({ label: g.label, signed: parseFloat((g.signed || 0).toFixed(2)) })),
        }));
      const byGame = (p.byGameOrder || []).map(label => ({ label, net: parseFloat((p.byGameMap[label] || 0).toFixed(2)) }));
      return { playerId, name: p.name, net: parseFloat(p.net.toFixed(2)), putts: p.putts, skins: p.skins, lines, evens, byGame };
    });
}

/* ============================================================================
 * Skins won-hole provenance: for each player, ordered list of contributing
 * holes with which were won outright vs carried in. Mirrors the verified
 * walk used in design. Returns { [playerId]: [{ hole, won }] }.
 * ==========================================================================*/
function skinsProvenance(holes) {
  const out = {};
  let pending = [];
  const ordered = [...holes].sort((a, b) => a.hole_number - b.hole_number);
  for (const h of ordered) {
    if (h.is_tied || !h.winner_id) { pending.push(h.hole_number); continue; }
    const w = h.winner_id;
    if (!out[w]) out[w] = [];
    for (const c of pending) out[w].push({ hole: c, won: false });
    out[w].push({ hole: h.hole_number, won: true });
    pending = [];
  }
  return out;
}

/* ---------------------------------------------------------------------------
 * Small presentational helpers
 * ------------------------------------------------------------------------- */
const C = {
  green: '#166534', greenText: '#0f6e56', red: '#a32d2d', dark: '#1f2937',
  grey: '#6b7280', tertiary: '#9ca3af', border: '#e5e7eb', soft: '#f9fafb',
  successBg: '#e1f5ee', netBg: '#ecfdf5', netBorder: '#a7f3d0',
};

function Money({ value, big }) {
  const pos = value >= 0;
  return (
    <span style={{ fontWeight: 700, fontSize: big ? 16 : 13, color: pos ? C.greenText : C.red, whiteSpace: 'nowrap' }}>
      {pos ? '+' : '\u2212'} ${Math.abs(value).toFixed(2)}
    </span>
  );
}

function CollapsibleScorecard({ title, collapsible, defaultOpen, children }) {
  const [open, setOpen] = useState(defaultOpen ?? !collapsible);
  if (!collapsible) {
    return <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', padding: '6px 0' }}>{children}</div>;
  }
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', marginTop: 4 }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 12px', background: '#f5f4ef', border: 'none', cursor: 'pointer',
          fontSize: 13, fontWeight: 600, color: C.dark }}
      >
        <span><span style={{ display: 'inline-block', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s', color: C.grey, marginRight: 6 }}>&#9656;</span>{title}</span>
        {!open && <span style={{ fontSize: 10.5, color: C.tertiary, fontWeight: 400 }}>tap to expand &middot; swipe sideways</span>}
      </button>
      {open && <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', padding: '8px 0 2px' }}>{children}</div>}
    </div>
  );
}

/* Generic 18-hole grid. rows = [{ name, sub, cells[18], out, in_, tot, totLabel, bold }] */
function ScoreGrid({ headerCells, rows, cellStyleFor, markFor, par, hcpIndex, cellCircle }) {
  const th = { padding: '3px 4px', textAlign: 'center', fontSize: 11, color: '#fff', background: C.dark, minWidth: 22 };
  const thOut = { ...th, background: '#374151', fontWeight: 700 };
  const nmTh = { padding: '4px 8px', textAlign: 'left', background: C.dark, color: '#fff', width: 120, minWidth: 120, position: 'sticky', left: 0, zIndex: 2 };
  const nmTd = { padding: '3px 8px', textAlign: 'left', fontSize: 11, width: 120, minWidth: 120, position: 'sticky', left: 0, zIndex: 1 };
  const td = { padding: '3px 1px', textAlign: 'center', fontSize: 11 };
  // Reference rows (par / hcp index): smaller, muted, non-striped.
  const refTd = { padding: '2px 1px', textAlign: 'center', fontSize: 9.5, color: C.grey };
  const refLabel = { ...nmTd, background: '#eef1f4', color: C.grey, fontSize: 9.5, fontWeight: 600, letterSpacing: '0.03em' };
  const refOut = { ...refTd, fontWeight: 600 };
  const sumRef = (a) => { if (!a) return null; const v = a.filter(x => x != null); return v.length ? v.reduce((s, x) => s + x, 0) : null; };
  const RefRow = ({ label, vals }) => vals ? (
    <tr style={{ background: '#eef1f4' }}>
      <td style={refLabel}>{label}</td>
      {vals.slice(0, 9).map((v, i) => <td key={i} style={refTd}>{v ?? '\u2014'}</td>)}
      <td style={refOut}>{label === 'PAR' ? (sumRef(vals.slice(0, 9)) ?? '') : ''}</td>
      <td style={{ width: 4, padding: 0 }} />
      {vals.slice(9).map((v, i) => <td key={i + 9} style={refTd}>{v ?? '\u2014'}</td>)}
      <td style={refOut}>{label === 'PAR' ? (sumRef(vals.slice(9)) ?? '') : ''}</td>
      <td style={refOut}>{label === 'PAR' ? (sumRef(vals) ?? '') : ''}</td>
    </tr>
  ) : null;
  // Won cells keep their green background AND get the number circled, like a
  // hand-marked scorecard. cellCircle(r, idx) decides which cells are circled.
  const renderCell = (r, c, idx) => {
    const circled = cellCircle && cellCircle(r, idx) && c != null;
    return (
      <>
        {circled
          ? <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 15, height: 15, padding: '0 1px', border: `1.5px solid ${C.green}`, borderRadius: '50%', boxSizing: 'border-box', lineHeight: 1, fontWeight: 700 }}>{c}</span>
          : (c ?? '\u2014')}
        {markFor ? markFor(r, idx) : null}
      </>
    );
  };
  return (
    <table style={{ borderCollapse: 'collapse', width: '100%', fontFamily: 'Arial, sans-serif', fontSize: 11, tableLayout: 'fixed', minWidth: 560 }}>
      <thead>
        <tr>
          <th style={nmTh}>PLAYER</th>
          {[1,2,3,4,5,6,7,8,9].map(h => <th key={h} style={th}>{h}</th>)}
          <th style={thOut}>OUT</th>
          <th style={{ background: '#0f172a', width: 4, padding: 0 }} />
          {[10,11,12,13,14,15,16,17,18].map(h => <th key={h} style={th}>{h}</th>)}
          <th style={thOut}>IN</th>
          <th style={{ ...thOut, background: C.green }}>{headerCells.totLabel}</th>
        </tr>
      </thead>
      <tbody>
        <RefRow label="PAR" vals={par} />
        <RefRow label="HCP" vals={hcpIndex} />
        {rows.map((r, ri) => (
          <tr key={ri} style={{ background: ri % 2 ? C.soft : '#fff' }}>
            <td style={{ ...nmTd, background: ri % 2 ? C.soft : '#fff', fontWeight: r.bold ? 700 : 400 }}>
              {r.name}{r.sub ? <span style={{ color: C.grey, fontSize: 10, marginLeft: 4 }}>{r.sub}</span> : null}
            </td>
            {r.cells.slice(0, 9).map((c, i) => <td key={i} style={{ ...td, ...(cellStyleFor ? cellStyleFor(r, i) : {}) }}>{renderCell(r, c, i)}</td>)}
            <td style={{ ...td, fontWeight: 700 }}>{r.out ?? '\u2014'}</td>
            <td style={{ width: 4, padding: 0 }} />
            {r.cells.slice(9).map((c, i) => <td key={i+9} style={{ ...td, ...(cellStyleFor ? cellStyleFor(r, i+9) : {}) }}>{renderCell(r, c, i+9)}</td>)}
            <td style={{ ...td, fontWeight: 700 }}>{r.in_ ?? '\u2014'}</td>
            <td style={{ ...td, fontWeight: 700 }}>{r.tot ?? '\u2014'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const sum = (a) => { const v = a.filter(x => x != null); return v.length ? v.reduce((s, x) => s + x, 0) : null; };

/* ---------------------------------------------------------------------------
 * CLOSEST TO THE PIN block (Sept 28, 2026): each CTP hole and who won it, the prize,
 * and each player's net for the game. A hole nobody claimed paid nothing.
 * ------------------------------------------------------------------------- */
function CtpBlock({ game }) {
  const res = game.results || {};
  const holes = res.ctp || [];
  const prize = res.prize || 0;
  const totals = res.totals || [];
  const n = totals.length;
  const stake = res.funding === 'per_player' ? (res.per_player || 0) : null;   // "$1 a man per par 3"
  return (
    <section style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: C.grey, letterSpacing: '0.04em' }}>
        CLOSEST TO THE PIN &middot; {prize > 0 ? (stake != null ? `$${stake.toFixed(2)} a player, a hole` : `$${prize.toFixed(2)} a hole`) : 'winners only'}{res.must_make_par ? ' \u00b7 must make par' : ''}
      </div>
      <div style={{ fontSize: 17, fontWeight: 600, margin: '2px 0 8px' }}>Closest to the pin</div>
      {holes.map(h => (
        <div key={h.hole_number} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 10px', borderRadius: 8, background: '#f5f4ef', marginBottom: 4, fontSize: 13 }}>
          <span><strong style={{ fontWeight: 700 }}>#{h.hole_number}</strong>&nbsp;&nbsp;{h.winner_name || '\u2014'}{h.set_by_organizer ? <span style={{ color: C.grey, fontSize: 11 }}> (set by the organizer)</span> : null}</span>
          <span style={{ fontWeight: 700, color: h.winner_name && prize > 0 ? C.greenText : C.tertiary }}>{h.winner_name && prize > 0 ? `$${prize.toFixed(2)}` : (h.winner_name ? '' : 'no winner')}</span>
        </div>
      ))}
      {prize > 0 && n > 0 && (
        <div style={{ fontSize: 11, color: C.grey, marginTop: 6, lineHeight: 1.45 }}>
          {stake != null
            ? <>Each of the {n} players in the game put in ${stake.toFixed(2)} a hole, so each hole&rsquo;s winner collects ${prize.toFixed(2)}. A hole nobody won pays nothing.</>
            : <>Each hole&rsquo;s winner collects ${prize.toFixed(2)}, paid equally by the {n} players in the game (${(prize / n).toFixed(2)} each). A hole nobody won pays nothing.</>}
        </div>
      )}
      {prize > 0 && totals.some(t => t.net_amount) && (
        <>
          <div style={{ fontSize: 12, color: C.grey, fontWeight: 600, margin: '10px 0 6px' }}>Net for the game</div>
          {totals.map(t => (
            <div key={t.player_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderRadius: 8, background: '#f5f4ef', marginBottom: 4 }}>
              <span style={{ fontSize: 13 }}>{t.player_name}{t.ctp_won ? <span style={{ color: C.grey, fontSize: 11 }}> — won {t.holes_won.map(x => `#${x}`).join(', ')}</span> : null}</span>
              <Money value={t.net_amount || 0} />
            </div>
          ))}
        </>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * Dots (Oct 6, 2026). The game was in the summary's "by game" line but had no
 * section of its own, so a player couldn't see where his dots came from. One row
 * per player: his dots, what they were (birdie / sandy / poley on which hole) and
 * his net. "Within each group" lists each group under its own heading — only
 * group-mates settle; "Whole field" is one list — everyone settles with everyone.
 * Reads the engine's totals: total_dots, breakdown, dot_holes, net_amount, and
 * group_id / group_name / in_group when settled within groups.
 * ------------------------------------------------------------------------- */
function DotsBlock({ game }) {
  const res = game.results || {};
  const totals = res.totals || [];
  const bet = parseFloat(res.bet_amount ?? game.bet_amount) || 0;
  const groupMode = res.mode === 'group';
  const counts = res.count || { birdies: true, sandies: true, polies: true };
  const kinds = [counts.birdies ? 'birdies' : null, counts.sandies ? 'sandies' : null, counts.polies ? 'polies' : null].filter(Boolean);
  const holesText = (dh) => Object.keys(dh || {})
    .map(Number).sort((a, b) => a - b)
    .map(h => `#${h} ${(dh[h] || []).join(' + ')}`).join(' · ');
  const row = (t) => (
    <div key={t.player_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 8, background: '#f5f4ef', marginBottom: 4 }}>
      <span style={{ minWidth: 30, textAlign: 'center', fontWeight: 700, fontSize: 15, color: t.total_dots ? C.dark : C.tertiary }}>{t.total_dots || 0}</span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
        {t.player_name}
        {t.total_dots ? <span style={{ display: 'block', color: C.grey, fontSize: 11, marginTop: 1 }}>{holesText(t.dot_holes) || t.breakdown}</span> : null}
        {groupMode && t.in_group === false ? <span style={{ display: 'block', color: C.grey, fontSize: 11 }}>Not in a group — settles with nobody</span> : null}
      </span>
      {bet > 0 && <Money value={t.net_amount || 0} />}
    </div>
  );
  let body;
  if (groupMode) {
    const order = [];
    const byGroup = {};
    for (const t of totals) {
      const k = t.in_group === false ? 'none' : String(t.group_id);
      if (!byGroup[k]) { byGroup[k] = { name: t.in_group === false ? 'Not in a group' : (t.group_name || 'Group'), rows: [] }; order.push(k); }
      byGroup[k].rows.push(t);
    }
    body = order.map(k => (
      <div key={k} style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: C.greenText, fontWeight: 700, margin: '6px 0 4px' }}>{byGroup[k].name}</div>
        {byGroup[k].rows.map(row)}
      </div>
    ));
  } else {
    body = totals.map(row);
  }
  return (
    <section style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: C.grey, letterSpacing: '0.04em' }}>
        DOTS &middot; {bet > 0 ? `$${bet.toFixed(2)} a dot` : 'no money'}{kinds.length ? ` · ${kinds.join(', ')}` : ''}
      </div>
      <div style={{ fontSize: 17, fontWeight: 600, margin: '2px 0 4px' }}>Dots</div>
      <div style={{ fontSize: 12, color: C.grey, marginBottom: 8, lineHeight: 1.45 }}>
        {groupMode
          ? 'Settled within each group: you settle only with the players in your own group.'
          : 'Settled across the whole field: you settle with every player in the game.'}
        {bet > 0 ? ` For each pair, the player with more dots collects $${bet.toFixed(2)} for every dot of difference.` : ''}
      </div>
      {body}
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * Stableford (Oct 6, 2026). Individuals: one row per player — place, points, money.
 * Teams: one block per team — place, team points, what each player collects or
 * pays — with each player's own points beneath. Before this, Stableford appeared
 * only in the summary's "by game" line.
 * ------------------------------------------------------------------------- */
function StablefordBlock({ game, collapsible }) {
  const res = game.results || {};
  const totals = res.totals || [];
  const bet = parseFloat(res.bet_amount ?? game.bet_amount) || 0;
  const pot = res.payout_mode === 'pot';
  const teams = res.team_mode === 'teams';
  const money = !res.results_only && bet > 0;
  const head = [
    'STABLEFORD',
    teams ? `TEAMS OF ${res.team_size} · ${String(res.team_count_label || '').toUpperCase()}` : null,
    res.competition === 'gross' ? 'GROSS' : `NET ${res.allowance_pct}%`,
    money ? (pot ? `$${bet.toFixed(2)} buy-in` : `$${bet.toFixed(2)} a point`) : 'results only',
  ].filter(Boolean).join(' · ');
  const rule = 'Points: double bogey or worse 0, bogey 1, par 2, birdie 3, eagle 4, albatross 5.';
  const row = (key, left, sub, right) => (
    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 8, background: '#f5f4ef', marginBottom: 4 }}>
      <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>{left}{sub ? <span style={{ display: 'block', color: C.grey, fontSize: 11, marginTop: 1 }}>{sub}</span> : null}</span>
      {right}
    </div>
  );
  const nameOf = (id) => (totals.find(t => t.player_id === id) || {}).player_name || '';
  // Within each group (Oct 7, 2026): a heading over each group's rows, as Dots.
  const grouped = res.mode === 'group';
  const withGroupHeads = (list, keyPfx, render) => {
    const out = [];
    let last;
    list.forEach(t => {
      if (grouped && t.group_id !== last) {
        out.push(<div key={`${keyPfx}g${t.group_id}`} style={{ fontSize: 11, fontWeight: 700, color: C.grey, textTransform: 'uppercase', letterSpacing: '0.04em', margin: '8px 0 4px' }}>{t.in_group ? (t.group_name || 'Group') : 'Not in a group'}</div>);
        last = t.group_id;
      }
      out.push(render(t));
    });
    return out;
  };
  // Front / back / overall (Oct 7, 2026): one list per part, each with its own places and
  // what that part paid. The players' totals across the parts are in the summary above.
  const partBody = (teamsList, totalsList, partMoney, keyPfx) => teams
    ? (teamsList || []).map(tm => {
        const members = (tm.member_ids || []).map(id => (totalsList || []).find(t => t.player_id === id)).filter(Boolean);
        return row(`${keyPfx}t${tm.team_id}`,
          <><strong style={{ fontWeight: 700 }}>{tm.place_label || '—'}</strong>&nbsp;&nbsp;{tm.name} &middot; {tm.points ?? '—'} pts</>,
          members.map(m => `${m.player_name} ${m.points ?? '—'}`).join(' · '),
          partMoney && tm.prize_each != null ? <span style={{ fontWeight: 700, color: tm.prize_each > 0 ? C.greenText : C.tertiary, whiteSpace: 'nowrap' }}>{tm.prize_each > 0 ? `$${tm.prize_each.toFixed(2)} each` : '—'}</span> : null);
      })
    : withGroupHeads((totalsList || []).filter(t => t.points != null), keyPfx, t => row(`${keyPfx}${t.player_id}`,
        <><strong style={{ fontWeight: 700 }}>{t.place_label || '—'}</strong>&nbsp;&nbsp;{t.player_name} &middot; {t.points} pts</>,
        null,
        partMoney ? <span style={{ fontWeight: 700, color: t.prize > 0 ? C.greenText : C.tertiary, whiteSpace: 'nowrap' }}>{t.prize > 0 ? `$${t.prize.toFixed(2)}` : '—'}</span> : null));
  let body;
  if (res.segments) {
    const segs = res.segments;
    body = segs.order.map(k => {
      const part = segs[k] || {};
      const partMoney = part.paid && !part.results_only;
      return (
        <div key={k} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.green, textTransform: 'uppercase', letterSpacing: '0.04em', margin: '6px 0 4px' }}>
            {part.label}{part.paid ? (part.buy_in > 0 ? ` · $${part.buy_in.toFixed(2)} of the buy-in` : '') : ' · not paid'}
          </div>
          {partBody(part.teams, part.totals, partMoney, k)}
        </div>
      );
    });
  } else if (teams) {
    body = (res.teams || []).map(tm => {
      const each = pot ? tm.prize_each : tm.net_each;
      const members = (tm.member_ids || []).map(id => totals.find(t => t.player_id === id)).filter(Boolean);
      return (
        <div key={tm.team_id} style={{ marginBottom: 8 }}>
          {row(`t${tm.team_id}`,
            <><strong style={{ fontWeight: 700 }}>{tm.place_label || '—'}</strong>&nbsp;&nbsp;{tm.name} &middot; {tm.points ?? '—'} pts</>,
            members.map(m => `${m.player_name} ${m.points ?? '—'}`).join(' · '),
            money && each != null ? (pot
              ? <span style={{ fontWeight: 700, color: each > 0 ? C.greenText : C.tertiary, whiteSpace: 'nowrap' }}>{each > 0 ? `$${each.toFixed(2)} each` : '—'}</span>
              : <span style={{ whiteSpace: 'nowrap' }}><Money value={each} /> <span style={{ fontSize: 11, color: C.grey }}>each</span></span>) : null)}
        </div>
      );
    });
  } else {
    body = withGroupHeads(totals.filter(t => t.points != null), 'b', t => row(t.player_id,
      <><strong style={{ fontWeight: 700 }}>{t.place_label || '—'}</strong>&nbsp;&nbsp;{t.player_name} &middot; {t.points} pts</>,
      null,
      money ? (pot ? <span style={{ fontWeight: 700, color: t.prize > 0 ? C.greenText : C.tertiary, whiteSpace: 'nowrap' }}>{t.prize > 0 ? `$${t.prize.toFixed(2)}` : '—'}</span> : <Money value={t.net_amount || 0} />) : null));
  }
  const how = teams
    ? (pot ? 'Everyone paid the buy-in; each team’s prize is split evenly among its players.' : 'Each player on the team with fewer points pays the stake for every point of difference; each player on the other team collects the same.')
    : grouped
    ? (pot ? 'Each group played its own Stableford: everyone paid the buy-in, and each group\u2019s places split that group\u2019s pot.' : res.payout_mode === 'per_point_field' ? 'Each group played its own Stableford: each player wins or pays his points against his group\u2019s average.' : 'Each group played its own Stableford: every pair in a group settles the difference in points.')
    : (pot ? 'Everyone paid the buy-in; the places split the pot.' : res.payout_mode === 'per_point_field' ? `Each player wins or pays his points against the field average (${res.field_average}).` : 'Every pair of players settles the difference in points.');
  const notOnTeam = teams ? (res.not_on_team || []) : [];
  // Team card (Oct 6, 2026 — Dennis: "so the players understand" why a team of two 10s
  // has 11): each team's points per hole in bold, its players' points beneath, and the
  // scores that counted for the team circled.
  const cardRows = [];
  if (teams) {
    const cellsOf = (hp) => Array.from({ length: 18 }, (_, i) => (hp && hp[i + 1] != null ? hp[i + 1] : null));
    const sums = (cells) => { const out = sum(cells.slice(0, 9)); const in_ = sum(cells.slice(9)); return { out, in_, tot: (out != null || in_ != null) ? (out || 0) + (in_ || 0) : null }; };
    for (const tm of (res.teams || [])) {
      const tc = cellsOf(tm.hole_points);
      cardRows.push({ name: tm.name, bold: true, cells: tc, ...sums(tc), _team: tm });
      for (const id of (tm.member_ids || [])) {
        const p = totals.find(t => t.player_id === id);
        if (!p) continue;
        const pc = cellsOf(p.hole_points);
        cardRows.push({ name: p.player_name, cells: pc, ...sums(pc), _pid: id, _team: tm });
      }
    }
  }
  const counted = (r, idx) => !!(r._pid && r._team && r._team.counted && (r._team.counted[idx + 1] || []).includes(r._pid));
  return (
    <section style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: C.grey, letterSpacing: '0.04em' }}>{head}</div>
      <div style={{ fontSize: 17, fontWeight: 600, margin: '2px 0 4px' }}>{teams ? 'Team Stableford' : 'Stableford'}</div>
      <div style={{ fontSize: 12, color: C.grey, marginBottom: 8, lineHeight: 1.45 }}>{rule}{money ? ` ${how}` : ''}</div>
      {body}
      {notOnTeam.length > 0 && <div style={{ fontSize: 11, color: C.grey, marginTop: 4 }}>Not on a team: {notOnTeam.map(x => x.player_name || nameOf(x.player_id)).join(', ')}.</div>}
      {cardRows.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <CollapsibleScorecard title="View team card" collapsible={collapsible}>
            <ScoreGrid headerCells={{ totLabel: 'TOT' }} rows={cardRows} cellCircle={counted} />
          </CollapsibleScorecard>
          <div style={{ fontSize: 11, color: C.grey, margin: '6px 0 0' }}>Points, hole by hole. Bold rows are the team; each player&rsquo;s own points are beneath, and the {res.team_count >= res.team_size ? 'scores' : `best ${res.team_count === 1 ? 'score' : `${res.team_count} scores`}`} that counted for the team on each hole {res.team_count >= res.team_size ? 'are' : (res.team_count === 1 ? 'is' : 'are')} circled. A team can total more than any one player because a different partner can count on each hole.</div>
        </div>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * Match Play (Oct 6, 2026): every match and its result — "won 3 & 2", "1 up",
 * "halved" — with what changes hands.
 * ------------------------------------------------------------------------- */
function MatchPlayBlock({ game }) {
  const res = game.results || {};
  const bet = parseFloat(res.bet_amount ?? game.bet_amount) || 0;
  const matches = res.matches || [];
  const strokes = res.handicap_basis === 'gross' ? 'gross' : res.handicap_basis === 'full' ? 'net, full course handicap' : (res.mode === 'teams' ? 'net, 90% off the low man' : 'net, off the low man');
  return (
    <section style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: C.grey, letterSpacing: '0.04em' }}>
        MATCH PLAY &middot; {res.mode === 'teams' ? 'FOUR-BALL' : 'SINGLES'} &middot; {strokes.toUpperCase()} &middot; {bet > 0 ? `$${bet.toFixed(2)} a match` : 'results only'}
      </div>
      <div style={{ fontSize: 17, fontWeight: 600, margin: '2px 0 8px' }}>Match play</div>
      {matches.map(m => {
        const winSide = m.winner === 'a' ? m.side_a : m.winner === 'b' ? m.side_b : null;
        const loseSide = m.winner === 'a' ? m.side_b : m.winner === 'b' ? m.side_a : null;
        const line = winSide
          ? <><strong style={{ fontWeight: 700 }}>{winSide.names.join(' & ')}</strong> beat {loseSide.names.join(' & ')}</>
          : <>{m.side_a.names.join(' & ')} vs {m.side_b.names.join(' & ')}</>;
        const result = m.decided ? (m.winner ? m.status : 'halved') : `not finished (${m.diff === 0 ? 'all square' : m.status} thru ${m.holes_played})`;
        return (
          <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 8, background: '#f5f4ef', marginBottom: 4 }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>{line}<span style={{ display: 'block', color: C.grey, fontSize: 11, marginTop: 1 }}>{result}</span></span>
            {bet > 0 && m.decided && (
              <span style={{ fontWeight: 700, fontSize: 13, color: winSide ? C.greenText : C.tertiary, whiteSpace: 'nowrap' }}>{winSide ? `$${bet.toFixed(2)}${winSide.names.length > 1 ? ' each' : ''}` : 'push'}</span>
            )}
          </div>
        );
      })}
      {(res.skipped || []).length > 0 && (
        <div style={{ fontSize: 11, color: C.grey, marginTop: 4 }}>No match for {(res.skipped || []).map(x => x.group_name || `group ${x.group_id}`).join(', ')} &mdash; {res.mode === 'teams' ? 'four-ball needs four players in the group' : 'a player alone in his group'}.</div>
      )}
      <div style={{ fontSize: 11, color: C.grey, marginTop: 6, lineHeight: 1.45 }}>A match is won by the side up by more holes than are left to play (&ldquo;3 &amp; 2&rdquo;); all square at the end is halved and pays nothing.{res.mode === 'teams' ? ' Four-ball: the side’s better score on each hole; each player settles with one player on the other side.' : ''}</div>
    </section>
  );
}

/* ============================================================================
 * MAIN COMPONENT
 * ==========================================================================*/
/* ============================================================================
 * "Your settlement" (Oct 7, 2026 — Dennis): in the player app the statement opens
 * on the reader's own result — his total in large type, by game, and only his
 * pay/collect lines — with a Full Settlement button for everything else. Built
 * from the SAME playerSummary row the full statement shows, so the two can never
 * disagree. Organizer screens and the email pass no viewerId and are unchanged.
 * Players who sat out the games never get here: the app shows them a message and
 * the server sends them no settlement.
 * ==========================================================================*/
function PersonalSettlement({ me, meta, onFull }) {
  const big = me.net >= 0;
  return (
    <div style={{ fontFamily: 'Arial, sans-serif', color: '#111' }}>
      <div style={{ background: C.green, color: '#fff', padding: '14px 18px', borderRadius: '10px 10px 0 0' }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Your settlement</div>
        <div style={{ fontSize: 13, opacity: 0.9, marginTop: 2 }}>{[meta.courseName, meta.dateLabel].filter(Boolean).join(' \u00b7 ')}</div>
      </div>
      <div style={{ border: `1px solid ${C.border}`, borderTop: 'none', borderRadius: '0 0 10px 10px', padding: 16, background: '#fff' }}>
        <div style={{ fontSize: 13, color: C.grey }}>Your result today, all games</div>
        <div style={{ fontSize: 26, fontWeight: 800, color: Math.abs(me.net) < 0.005 ? C.dark : (big ? C.greenText : C.red), lineHeight: 1.15, margin: '2px 0 6px' }}>
          {Math.abs(me.net) < 0.005 ? 'Even' : `${big ? '+' : '\u2212'} $${Math.abs(me.net).toFixed(2)}`}
        </div>
        {me.byGame && me.byGame.length >= 1 && (
          <div style={{ fontSize: 14, color: C.grey, marginBottom: 10 }}>
            {me.byGame.map((bg, bi) => (
              <span key={bg.label}>
                {bi > 0 && <span style={{ color: C.tertiary }}> &middot; </span>}
                {bg.label}{' '}
                <span style={{ fontWeight: 700, color: bg.net >= 0 ? C.greenText : C.red }}>{bg.net >= 0 ? '+' : '\u2212'}${Math.abs(bg.net).toFixed(2)}</span>
              </span>
            ))}
          </div>
        )}
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {me.lines.length === 0 && (
            <div style={{ fontSize: 16, color: C.dark }}>You're even today &mdash; nothing to pay or collect.</div>
          )}
          {me.lines.map(l => (
            <div key={l.cpId} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ color: l.isCollect ? C.greenText : C.red, fontSize: 20, lineHeight: 1.1, flexShrink: 0 }}>{l.isCollect ? '\u2199' : '\u2197'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16 }}>{l.isCollect ? 'Collect from' : 'Pay'} <span style={{ fontWeight: 700 }}>{l.name}</span></div>
                {l.detail ? <div style={{ fontSize: 12, color: C.tertiary, marginTop: 1 }}>{l.detail}</div> : null}
              </div>
              <span style={{ fontSize: 17, fontWeight: 800, color: l.isCollect ? C.greenText : C.red, whiteSpace: 'nowrap' }}>${Math.abs(l.net).toFixed(2)}</span>
            </div>
          ))}
        </div>
        <button type="button" onClick={onFull}
          style={{ width: '100%', minHeight: 50, marginTop: 16, borderRadius: 10, border: `1px solid ${C.green}`, background: '#fff', color: C.green, fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
          Full Settlement
        </button>
        <div style={{ fontSize: 12, color: C.grey, textAlign: 'center', marginTop: 6 }}>Everyone's lines, the scorecards and every game, hole by hole.</div>
      </div>
    </div>
  );
}

export default function SettlementStatement({ games = [], playerSummary = [], scorecard = null, meta = {}, collapsibleScorecards = true, viewerId = null }) {
  const [showFull, setShowFull] = useState(false);
  const me = viewerId ? playerSummary.find(p => String(p.playerId) === String(viewerId)) : null;
  if (me && !showFull) return <PersonalSettlement me={me} meta={meta} onFull={() => setShowFull(true)} />;
  return <FullSettlement games={games} playerSummary={playerSummary} scorecard={scorecard} meta={meta} collapsibleScorecards={collapsibleScorecards}
    onMine={me ? () => setShowFull(false) : null} />;
}

function FullSettlement({ games = [], playerSummary = [], scorecard = null, meta = {}, collapsibleScorecards = true, onMine = null }) {
  const skinsGames = games.filter(g => g.game_type?.startsWith('skins'));
  const puttsGames = games.filter(g => g.game_type === 'putts');
  const strokeGames = games.filter(g => g.game_type === 'stroke_play');
  const bestBallGames = games.filter(g => isTeamBestBall(g.game_type));   // Best Ball + Shamble (Sept 28, 2026)
  const ctpGames = games.filter(g => g.game_type === 'ctp');               // Closest to the Pin (Sept 28, 2026)
  const dotsGames = games.filter(g => g.game_type === 'dots');             // Dots (Oct 6, 2026)
  const stablefordGames = games.filter(g => g.game_type === 'stableford'); // Stableford, individuals or teams (Oct 6, 2026)
  const matchPlayGames = games.filter(g => g.game_type === 'match_play');  // Match Play (Oct 6, 2026)

  // Players who appear in any game (for the gross card).
  const grossPlayers = [];
  const seen = new Set();
  for (const g of games) {
    for (const t of (g.results?.totals || [])) {
      if (!seen.has(t.player_id)) { seen.add(t.player_id); grossPlayers.push(t); }
    }
  }

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', color: '#111' }}>
      {onMine && (
        <button type="button" onClick={onMine}
          style={{ width: '100%', minHeight: 44, marginBottom: 10, borderRadius: 10, border: `1px solid ${C.border}`, background: '#fff', color: C.green, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
          &lsaquo; Back to your settlement
        </button>
      )}

      {/* Header */}
      <div style={{ background: C.green, color: '#fff', padding: '14px 18px', borderRadius: '10px 10px 0 0' }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Round Settlement{meta.courseName ? ` — ${meta.courseName}` : ''}</div>
        <div style={{ fontSize: 12, opacity: 0.88, marginTop: 2 }}>
          {[meta.dateLabel, meta.teeLabel, 'group settlement'].filter(Boolean).join(' \u00b7 ')}
        </div>
        {meta.finalizedAtLabel && (
          <span style={{ display: 'inline-block', fontSize: 11, background: 'rgba(255,255,255,0.18)', padding: '2px 9px', borderRadius: 10, marginTop: 6 }}>
            &#128274; Round finalized &middot; {meta.finalizedAtLabel}
          </span>
        )}
      </div>

      <div style={{ border: `1px solid ${C.border}`, borderTop: 'none', borderRadius: '0 0 10px 10px', padding: 14 }}>

        {/* ---- 1. CONSOLIDATED SETTLEMENT SUMMARY ---- */}
        <section style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: C.grey, letterSpacing: '0.04em' }}>SETTLEMENT SUMMARY &middot; ALL GAMES</div>
          <div style={{ fontSize: 17, fontWeight: 600, margin: '2px 0' }}>Dollars to be exchanged</div>
          <div style={{ fontSize: 12, color: C.grey, marginBottom: 4 }}>Every game netted together. Find your name — each line is who you pay or collect from, and why.</div>

          {playerSummary.map((p, i) => (
            <div key={p.playerId} style={{ borderBottom: i < playerSummary.length - 1 ? `1px solid ${C.border}` : 'none', padding: '12px 2px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <span style={{ fontSize: 15, fontWeight: 600 }}>{p.name}</span>
                  {(p.skins != null || p.putts != null) && (
                    <span style={{ fontSize: 12, color: C.grey, marginLeft: 6 }}>
                      {[p.skins != null ? `${p.skins} skin${p.skins !== 1 ? 's' : ''}` : null,
                        p.putts != null ? `${p.putts} putts` : null].filter(Boolean).join(' \u00b7 ')}
                    </span>
                  )}
                </div>
                <Money value={p.net} big />
              </div>
              {p.byGame && p.byGame.length >= 2 && (
                <div style={{ marginTop: 5, fontSize: 12, color: C.grey }}>
                  <span style={{ color: C.tertiary }}>by game: </span>
                  {p.byGame.map((bg, bi) => (
                    <span key={bg.label}>
                      {bi > 0 && <span style={{ color: C.tertiary }}> &middot; </span>}
                      {bg.label}{' '}
                      <span style={{ fontWeight: 600, color: bg.net >= 0 ? C.greenText : C.red }}>
                        {bg.net >= 0 ? '+' : '\u2212'}${Math.abs(bg.net).toFixed(2)}
                      </span>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
                {p.lines.length === 0 && (!p.evens || p.evens.length === 0) && <div style={{ fontSize: 13, color: C.grey }}>Even — nothing to settle.</div>}
                {p.lines.map(l => (
                  <div key={l.cpId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <span style={{ color: l.isCollect ? C.greenText : C.red, fontSize: 16, flexShrink: 0 }}>{l.isCollect ? '\u2199' : '\u2197'}</span>
                    <span>{l.isCollect ? 'Collect from' : 'Pay'} <span style={{ fontWeight: 600 }}>{l.name}</span></span>
                    {l.detail ? <span style={{ color: C.tertiary, fontSize: 11 }}>{l.detail}</span> : null}
                    <span style={{ marginLeft: 'auto', fontWeight: 600, color: l.isCollect ? C.greenText : C.red, whiteSpace: 'nowrap' }}>
                      ${Math.abs(l.net).toFixed(2)}
                    </span>
                  </div>
                ))}
                {(p.evens || []).map(e => (
                  <div key={e.cpId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.grey }}>
                    <span style={{ color: C.tertiary, fontSize: 15, flexShrink: 0, width: 16, textAlign: 'center' }}>=</span>
                    <span>Even with <span style={{ fontWeight: 600, color: C.dark }}>{e.name}</span>
                      <span style={{ color: C.tertiary }}>{' \u2014 '}{e.parts.map(pt => `${pt.label} ${pt.signed >= 0 ? '+' : '\u2212'}$${Math.abs(pt.signed).toFixed(2)}`).join(', ')}</span>
                    </span>
                    <span style={{ marginLeft: 'auto', color: C.tertiary, fontWeight: 600, whiteSpace: 'nowrap' }}>$0.00</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div style={{ fontSize: 11, color: C.grey, marginTop: 8 }}>
            Amounts are netted across all games, so each pair settles with a single payment. Everyone&rsquo;s total nets to zero.
          </div>
        </section>

        {/* ---- 2. GROSS SCORECARD ---- */}
        <section style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: C.grey, letterSpacing: '0.04em' }}>REFERENCE</div>
          <div style={{ fontSize: 17, fontWeight: 600, margin: '2px 0' }}>Gross scorecard</div>
          <div style={{ fontSize: 12, color: C.grey, marginBottom: 4 }}>Everyone who played in any game. Raw scores, no strokes applied.</div>
          <CollapsibleScorecard title="View scorecard" collapsible={collapsibleScorecards}>
            <GrossGrid scorecard={scorecard} />
          </CollapsibleScorecard>
          <div style={{ fontSize: 11, color: C.grey, marginTop: 6 }}>Everyone on the tee sheet is shown. Dashes mean a player didn&rsquo;t use live scoring for that round.</div>
        </section>

        {/* ---- 3. STROKE PLAY BLOCKS (Sept 23, 2026) — the official placings first ---- */}
        {strokeGames.map(g => <StrokePlayBlock key={g.game_id} game={g} collapsible={collapsibleScorecards} />)}
        {bestBallGames.map(g => <BestBallBlock key={g.game_id} game={g} collapsible={collapsibleScorecards} />)}

        {/* ---- 3a. SKINS BLOCKS ---- */}
        {skinsGames.map(g => <SkinsBlock key={g.game_id} game={g} collapsible={collapsibleScorecards} />)}

        {/* ---- 3b. PUTTS BLOCKS ---- */}
        {puttsGames.map(g => <PuttsBlock key={g.game_id} game={g} collapsible={collapsibleScorecards} />)}
        {ctpGames.map(g => <CtpBlock key={g.game_id} game={g} />)}
        {dotsGames.map(g => <DotsBlock key={g.game_id} game={g} />)}
        {stablefordGames.map(g => <StablefordBlock key={g.game_id} game={g} collapsible={collapsibleScorecards} />)}
        {matchPlayGames.map(g => <MatchPlayBlock key={g.game_id} game={g} />)}

        {/* "est" footnote (Sept 23, 2026): a nine-hole round whose tees have no official
            front/back-nine ratings on file. The handicaps were derived from the 18-hole
            rating (handicapCalc.estimatedNineRatings). */}
        {(games.some(g => g.nine_hole_estimated) || (scorecard && scorecard.nine_hole_estimated)) && (
          <div style={{ fontSize: 11, color: C.grey, lineHeight: 1.5, margin: '0 2px 10px' }}>
            <strong style={{ fontWeight: 700 }}>est</strong> &mdash; 9-hole handicap estimated from the course&rsquo;s 18-hole rating; official 9-hole ratings aren&rsquo;t on file.
          </div>
        )}

        <div style={{ textAlign: 'center', fontSize: 11, color: C.tertiary, marginTop: 4 }}>Generated by Tee Time Organizer &middot; teetimeorganizer.net</div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Gross scorecard sourced from the round-wide `scorecard` block returned by
 * the standings endpoint. Shows EVERY confirmed tee-sheet player; unscored
 * players render as dashes (gross cells are null).
 * ------------------------------------------------------------------------- */
function GrossGrid({ scorecard }) {
  if (!scorecard || !scorecard.players) {
    return <div style={{ fontSize: 12, color: C.grey, padding: '8px 0' }}>Scorecard unavailable.</div>;
  }
  const rows = scorecard.players.map(p => {
    const cells = (p.gross || []).map(v => v ?? null);
    const out = sum(cells.slice(0, 9)); const in_ = sum(cells.slice(9));
    const tot = (out != null || in_ != null) ? (out || 0) + (in_ || 0) : null;
    const sub = p.course_hcp != null ? `HCP ${p.course_hcp}${p.course_hcp_estimated ? ' est' : ''}` : null;
    return { name: p.player_name, sub, cells, out, in_, tot, bold: true, picked: Array.isArray(p.picked) ? p.picked : null };
  });
  // A picked-up hole reads "7x" (Sept 27, 2026): the number, then x.
  const markFor = (r, idx) => (r.picked && r.picked[idx] && r.cells[idx] != null ? 'x' : null);
  return <ScoreGrid headerCells={{ totLabel: 'TOT' }} rows={rows} par={scorecard.par} hcpIndex={scorecard.hcp_index} markFor={markFor} />;
}

/* ---------------------------------------------------------------------------
 * SKINS block: net-method note + participant-scoped net scorecard (from
 * engine holes, already correct) + skins-won provenance + dollars.
 * ------------------------------------------------------------------------- */
function SkinsBlock({ game, collapsible }) {
  const holes = game.results?.holes || [];
  const totals = game.results?.totals || [];
  const prov = skinsProvenance(holes);
  const bet = parseFloat(game.bet_amount || 0);

  // Variant detection. Pot games ($X buy-in, pot split by skins, no carryover)
  // render differently from per-skin carryover/no-carryover games.
  const isPot = game.game_type === 'skins_pot' || game.game_type === 'skins_pot_gross';
  const isGrossPot = game.game_type === 'skins_pot_gross';
  const isCarryover = game.game_type === 'skins_carryover' || game.game_type === 'skins_carryover_gross';
  // How strokes were given (Sept 29, 2026): 'gross', 'low_man' (the default) or 'full'.
  const isGross = typeof game.game_type === 'string' && game.game_type.endsWith('_gross');
  const hcpBasis = isGross ? 'gross' : ((game.results?.handicap_basis === 'full' || game.settings?.handicap_basis === 'full') ? 'full' : 'low_man');

  // Pot math for the explainer line.
  const totalSkins = holes.filter(h => !h.is_tied && h.winner_id).length;
  const numPlayers = (totals || []).length;
  const totalPot = bet * numPlayers;
  const skinValue = totalSkins > 0 ? totalPot / totalSkins : 0;

  // Determine low player name for the net-method note (participant baseline).
  const lowName = game.low_player_name || game.results?.low_player_name || (totals.find(t => t.is_low)?.player_name) || null;

  // Net scorecard rows from engine holes (participant-scoped, correct strokes).
  const playerIds = [];
  const nameById = {};
  for (const h of holes) for (const ns of (h.net_scores || [])) {
    if (!nameById[ns.player_id]) { nameById[ns.player_id] = ns.player_name; playerIds.push(ns.player_id); }
  }
  const netByHole = {}; // playerId -> { hole: { net, won } }
  const parArr = Array(18).fill(null);
  const hcpArr = Array(18).fill(null);
  for (const h of holes) {
    const hi = h.hole_number - 1;
    if (hi >= 0 && hi < 18) {
      if (h.par != null) parArr[hi] = h.par;
      if (h.handicap_index != null) hcpArr[hi] = h.handicap_index;
    }
    for (const ns of (h.net_scores || [])) {
      netByHole[ns.player_id] = netByHole[ns.player_id] || {};
      netByHole[ns.player_id][h.hole_number] = { net: ns.net, won: !h.is_tied && h.winner_id === ns.player_id, strokes: ns.strokes_received || 0 };
    }
  }
  // For gross pot, the "net" cell is really gross (no strokes); show gross then.
  const cellValue = (pid, holeNum) => {
    const e = netByHole[pid]?.[holeNum];
    if (!e) return null;
    return e.net;
  };
  const rows = playerIds.map(pid => {
    const cells = Array.from({ length: 18 }, (_, i) => cellValue(pid, i + 1));
    const out = sum(cells.slice(0, 9)); const in_ = sum(cells.slice(9));
    const tot = (out != null || in_ != null) ? (out || 0) + (in_ || 0) : null;
    return { name: nameById[pid], cells, out, in_, tot, _pid: pid };
  });
  const cellStyleFor = (r, idx) => netByHole[r._pid]?.[idx + 1]?.won ? { background: C.successBg } : {};
  const markFor = (r, idx) => {
    const s = netByHole[r._pid]?.[idx + 1]?.strokes || 0;
    return s > 0 ? <sup style={{ fontSize: 7, color: '#16a34a', fontWeight: 700 }}>{'*'.repeat(s)}</sup> : null;
  };

  // Header sublabel: pot = "$X buy-in", per-skin = "$X / skin".
  const headerRate = isPot ? `$${bet.toFixed(2)} buy-in` : `$${bet.toFixed(2)} / skin`;
  // Won-cell caption varies by variant.
  const shown = isGross ? 'Gross shown.' : 'Net shown.';
  const cellCaption = isPot
    ? `Green cell = hole won (ties void — no carryover in a pot game). ${shown}`
    : (isCarryover
        ? `Green cell = hole won outright (after carryovers). ${shown}`
        : `Green cell = hole won outright. ${shown}`);

  return (
    <section style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: C.grey, letterSpacing: '0.04em' }}>
        {(game.display_name || 'Skins').toUpperCase()} &middot; {headerRate}
      </div>
      <div style={{ fontSize: 17, fontWeight: 600, margin: '2px 0' }}>Skins settlement</div>

      {isPot && (
        <div style={{ padding: '8px 10px', borderLeft: `4px solid ${C.green}`, background: C.netBg, border: `1px solid ${C.netBorder}`, borderLeftWidth: 4, fontSize: 11.5, color: '#065f46', margin: '8px 0 10px', lineHeight: 1.45 }}>
          <strong style={{ fontWeight: 700 }}>Pot game.</strong> Each player buys in ${bet.toFixed(2)}{numPlayers ? ` — $${totalPot.toFixed(2)} pot` : ''}. Ties void (no carryover). The pot is split among skin-winners by skins won{totalSkins > 0 ? `: $${totalPot.toFixed(2)} ÷ ${totalSkins} skin${totalSkins !== 1 ? 's' : ''} = $${skinValue.toFixed(2)} per skin` : ''}.
          {!isGrossPot && hcpBasis === 'full' && <span> Every player gets his full course handicap; <sup style={{ fontSize: 8, color: '#16a34a', fontWeight: 700 }}>*</sup> marks a stroke hole.</span>}
          {!isGrossPot && hcpBasis !== 'full' && <span> Strokes off the low player{lowName ? ` (${lowName})` : ''}; <sup style={{ fontSize: 8, color: '#16a34a', fontWeight: 700 }}>*</sup> marks a stroke hole.</span>}
          {isGrossPot && <span> Played at gross (no handicap strokes).</span>}
        </div>
      )}

      {game.nine_hole_estimated && (
        <div style={{ fontSize: 11, color: C.grey, margin: '6px 0 0' }}>Strokes use an <strong style={{ fontWeight: 700 }}>est</strong>imated 9-hole handicap &mdash; see the note at the foot of this statement.</div>
      )}

      {!isPot && hcpBasis === 'gross' && (
        <div style={{ padding: '7px 10px', borderLeft: `4px solid ${C.green}`, background: C.netBg, border: `1px solid ${C.netBorder}`, borderLeftWidth: 4, fontSize: 11, color: '#065f46', margin: '8px 0 10px', lineHeight: 1.4 }}>
          <strong style={{ fontWeight: 700 }}>Gross — no handicap strokes.</strong> Every player plays off scratch, exactly as the skins are settled.
        </div>
      )}
      {!isPot && hcpBasis === 'full' && (
        <div style={{ padding: '7px 10px', borderLeft: `4px solid ${C.green}`, background: C.netBg, border: `1px solid ${C.netBorder}`, borderLeftWidth: 4, fontSize: 11, color: '#065f46', margin: '8px 0 10px', lineHeight: 1.4 }}>
          <strong style={{ fontWeight: 700 }}>Net method — full course handicap.</strong> Every player gets his own course handicap, on the hardest holes of his tee, exactly as the skins are settled. <span style={{ whiteSpace: 'nowrap' }}><sup style={{ fontSize: 8, color: '#16a34a', fontWeight: 700 }}>*</sup> marks a hole where the player received a stroke.</span>
        </div>
      )}
      {!isPot && hcpBasis === 'low_man' && (
        <div style={{ padding: '7px 10px', borderLeft: `4px solid ${C.green}`, background: C.netBg, border: `1px solid ${C.netBorder}`, borderLeftWidth: 4, fontSize: 11, color: '#065f46', margin: '8px 0 10px', lineHeight: 1.4 }}>
          <strong style={{ fontWeight: 700 }}>Net method — strokes off the low player{lowName ? ` (${lowName})` : ''}.</strong> Strokes are figured relative to the lowest handicap among this game&rsquo;s players, exactly as the skins are settled. <span style={{ whiteSpace: 'nowrap' }}><sup style={{ fontSize: 8, color: '#16a34a', fontWeight: 700 }}>*</sup> marks a hole where the player received a stroke.</span>
        </div>
      )}

      <CollapsibleScorecard title={isGross ? 'View scorecard' : 'View net scorecard'} collapsible={collapsible}>
        <ScoreGrid headerCells={{ totLabel: 'TOT' }} rows={rows} cellStyleFor={cellStyleFor} markFor={markFor} par={parArr} hcpIndex={hcpArr} cellCircle={(r, idx) => !!netByHole[r._pid]?.[idx + 1]?.won} />
      </CollapsibleScorecard>
      <div style={{ fontSize: 11, color: C.grey, margin: '6px 0 10px' }}>{cellCaption}</div>

      <div style={{ fontSize: 12, color: C.grey, fontWeight: 600, margin: '12px 0 6px' }}>Skins won</div>
      {totals.filter(t => (t.skins_won || 0) > 0 || (t.net_amount || 0) !== 0).map(t => {
        // Pot games have no carryover — show only the holes actually won.
        // Carryover/no-carryover use the provenance walk (won + carried-in).
        const holesList = isPot
          ? holes.filter(h => !h.is_tied && h.winner_id === t.player_id)
                 .map(h => ({ hole: h.hole_number, won: true }))
          : (prov[t.player_id] || []);
        return (
          <div key={t.player_id} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: '9px 10px', borderRadius: 8, background: '#f5f4ef', marginBottom: 5 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{t.player_name} — {t.skins_won || 0} skins{isPot && t.pot_share != null ? ` · $${parseFloat(t.pot_share).toFixed(2)} of pot` : ''}</div>
              <div style={{ marginTop: 4, lineHeight: 1.5 }}>
                {holesList.map((h, i) => (
                  <span key={i} style={h.won
                    ? { fontSize: 13, fontWeight: 600, color: C.greenText }
                    : { fontSize: 10.5, color: C.tertiary }}>
                    {h.won ? `H${h.hole}` : `H${h.hole}`}{i < holesList.length - 1 ? <span style={{ fontSize: 10.5, color: C.tertiary }}>, </span> : ''}
                  </span>
                ))}
              </div>
            </div>
            <Money value={t.net_amount || 0} />
          </div>
        );
      })}
      <div style={{ fontSize: 10.5, color: C.grey, marginTop: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.greenText }}>H12</span> larger green = hole won outright &middot; <span style={{ fontSize: 10.5, color: C.tertiary }}>H4</span> smaller gray = tied hole carried into it.
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * STROKE PLAY block (Sept 23, 2026 — Stroke_Play_Design_v1): the placings for
 * each ranked competition (net / gross), the prize each place took, the net
 * scorecard with stroke marks, and how ties were decided.
 * ------------------------------------------------------------------------- */
const fmtVs = (v) => (v == null ? '' : v === 0 ? 'E' : v > 0 ? `+${v}` : `${v}`);

// Front / Back / Overall (Sept 28, 2026): the placings come one block per part — each
// nine (and the overall) with its own places and prizes. Money totals stay the game's.
function statementParts(res) {
  const segs = res && res.segments;
  if (!segs) return [{ key: 'round', label: null, totals: res.totals || [], purses: res.purses }];
  return segs.order.map(k => ({ key: k, label: `${segs[k].label}${segs[k].paid ? '' : ' (not paid)'}`, totals: segs[k].totals || [], purses: segs[k].purses }));
}
function segmentPayText(res) {
  const segs = res.segments;
  return segs.paid.map(k => {
    const s = segs[k];
    const purse = s.purses && s.purses[res.competition === 'gross' ? 'gross' : 'net'];
    const how = res.payout_mode === 'winner' ? 'winner takes all' : (s.places_pct || []).map(p => `${p}%`).join(' / ');
    return `${s.label}: $${(s.buy_in || 0).toFixed(2)} a player${purse && purse.purse ? `, $${purse.purse.toFixed(2)} purse` : ''}, ${how}`;
  }).join('; ') + '.';
}

function StrokePlayBlock({ game, collapsible }) {
  const res = game.results || {};
  const totals = res.totals || [];
  const holes = res.holes || [];
  const bet = parseFloat(game.bet_amount || 0);
  const competition = res.competition || 'net';
  const comps = competition === 'both' ? ['net', 'gross'] : [competition];
  const resultsOnly = res.results_only !== false;
  const n = totals.length;

  // Net scorecard rows from engine holes (participant-scoped, playing-handicap strokes).
  const playerIds = []; const nameById = {}; const netByHole = {};
  const parArr = Array(18).fill(null); const hcpArr = Array(18).fill(null);
  for (const h of holes) {
    const hi = h.hole_number - 1;
    if (hi >= 0 && hi < 18) { if (h.par != null) parArr[hi] = h.par; if (h.handicap_index != null) hcpArr[hi] = h.handicap_index; }
    for (const ns of (h.net_scores || [])) {
      if (!nameById[ns.player_id]) { nameById[ns.player_id] = ns.player_name; playerIds.push(ns.player_id); }
      netByHole[ns.player_id] = netByHole[ns.player_id] || {};
      netByHole[ns.player_id][h.hole_number] = { net: ns.net, gross: ns.gross, strokes: ns.strokes_received || 0 };
    }
  }
  const showNet = competition !== 'gross';
  const rows = playerIds.map(pid => {
    const cells = Array.from({ length: 18 }, (_, i) => { const e = netByHole[pid]?.[i + 1]; return e ? (showNet ? e.net : e.gross) : null; });
    const out = sum(cells.slice(0, 9)); const in_ = sum(cells.slice(9));
    const tot = (out != null || in_ != null) ? (out || 0) + (in_ || 0) : null;
    const t = totals.find(x => x.player_id === pid);
    const sub = showNet && t && t.playing_handicap != null ? `PH ${t.playing_handicap}` : null;
    return { name: nameById[pid], sub, cells, out, in_, tot, _pid: pid };
  });
  const markFor = (r, idx) => {
    if (!showNet) return null;
    const s = netByHole[r._pid]?.[idx + 1]?.strokes || 0;
    return s > 0 ? <sup style={{ fontSize: 7, color: '#16a34a', fontWeight: 700 }}>{'*'.repeat(s)}</sup> : null;
  };
  const how = res.tie_break === 'usga' ? 'the USGA scorecard playoff (last 9, then 6, 3 and the last hole)'
    : res.tie_break === 'hole_handicap' ? 'a match of cards, hardest hole first'
    : res.tie_break === 'committee' ? 'the organizer' : null;

  return (
    <section style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: C.grey, letterSpacing: '0.04em' }}>
        {(game.display_name || 'Stroke Play').toUpperCase()} &middot; {resultsOnly ? 'placings only' : `$${bet.toFixed(2)} buy-in`}
      </div>
      <div style={{ fontSize: 17, fontWeight: 600, margin: '2px 0' }}>{resultsOnly ? 'Stroke play results' : 'Stroke play settlement'}</div>

      <div style={{ padding: '8px 10px', borderLeft: `4px solid ${C.green}`, background: C.netBg, border: `1px solid ${C.netBorder}`, borderLeftWidth: 4, fontSize: 11.5, color: '#065f46', margin: '8px 0 10px', lineHeight: 1.45 }}>
        {showNet && (
          <span><strong style={{ fontWeight: 700 }}>Net — {res.allowance_pct}% playing handicap.</strong> Each player&rsquo;s course handicap times {res.allowance_pct}%, rounded once; strokes on the hardest holes of his own tee (<sup style={{ fontSize: 8, color: '#16a34a', fontWeight: 700 }}>*</sup> marks a stroke hole). </span>
        )}
        {competition === 'gross' && <span><strong style={{ fontWeight: 700 }}>Gross.</strong> No handicap strokes. </span>}
        {competition === 'both' && <span>Gross is ranked as well — a player may place in both. </span>}
        {!resultsOnly && (
          <span>
            {res.segments ? segmentPayText(res) : <>
            {res.payout_mode === 'winner' ? 'Winner takes all' : `Paid places ${(res.places_pct || []).map(p => `${p}%`).join(' / ')}`}
            {competition === 'both' && res.purses ? ` — $${(res.purses.net.purse || 0).toFixed(2)} net purse, $${(res.purses.gross.purse || 0).toFixed(2)} gross purse` : res.purses ? ` of the $${((res.purses[competition] || {}).purse || 0).toFixed(2)} purse` : ''}.</>}
            {res.money_ties === 'split' ? ' Tied scores share the money for the places they occupy.' : ' Each decided place is paid its own share.'}
          </span>
        )}
        {how && <span> Ties decided by {how}; SP = decided on the cards, PO = by playoff or the organizer.</span>}
      </div>
      {game.nine_hole_estimated && (
        <div style={{ fontSize: 11, color: C.grey, margin: '0 0 8px' }}>Strokes use an <strong style={{ fontWeight: 700 }}>est</strong>imated 9-hole handicap &mdash; see the note at the foot of this statement.</div>
      )}

      {statementParts(res).flatMap(part => comps.map(comp => {
        const ranked = part.totals.filter(t => t[`${comp}_place`] != null).sort((a, b) => a[`${comp}_place`] - b[`${comp}_place`]);
        const unranked = part.totals.filter(t => t[`${comp}_place`] == null);
        const purse = part.purses && part.purses[comp] ? part.purses[comp] : null;
        const unalloc = purse && purse.unallocated_pct > 0 ? purse.unallocated_pct : 0;
        return (
          <div key={`${part.key}-${comp}`}>
            <div style={{ fontSize: 12, color: part.label ? C.greenText : C.grey, fontWeight: 600, margin: '12px 0 6px' }}>
              {part.label ? `${part.label} — ` : ''}{comps.length > 1 ? (comp === 'net' ? 'Net placings' : 'Gross placings') : 'Placings'}
            </div>
            {ranked.map(t => {
              const prize = t[`prize_${comp}`];
              const mark = t[`${comp}_tie_mark`];
              const pending = t[`${comp}_tie_status`] === 'pending';
              const score = comp === 'net'
                ? `${t.net_total ?? '—'} net${t.net_vs_par != null ? ` (${fmtVs(t.net_vs_par)})` : ''} · ${t.gross_total ?? '—'} gross${t.playing_handicap != null ? ` · PH ${t.playing_handicap}` : ''}`
                : `${t.gross_total ?? '—'} gross${t.gross_vs_par != null ? ` (${fmtVs(t.gross_vs_par)})` : ''}`;
              return (
                <div key={t.player_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 10px', borderRadius: 8, background: '#f5f4ef', marginBottom: 5 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, minWidth: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: C.dark, minWidth: 30 }}>{t[`${comp}_place_label`]}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{t.player_name}{mark ? <span style={{ fontSize: 10, color: C.grey, marginLeft: 6 }}>{mark}</span> : null}{pending ? <span style={{ fontSize: 10, color: '#b45309', marginLeft: 6 }}>playoff pending</span> : null}</div>
                      <div style={{ fontSize: 11, color: C.grey }}>{score}</div>
                    </div>
                  </div>
                  {prize != null ? (
                    <span style={{ fontWeight: 700, fontSize: 13, color: prize > 0 ? C.greenText : C.tertiary, whiteSpace: 'nowrap' }}>{prize > 0 ? `$${prize.toFixed(2)}` : '—'}</span>
                  ) : null}
                </div>
              );
            })}
            {unranked.map(t => (
              <div key={t.player_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 10px', borderRadius: 8, background: '#f9fafb', marginBottom: 5, color: C.grey, fontSize: 12 }}>
                <span>{t.player_name} — not ranked{comp === 'net' && t.net_total == null ? ' (no handicap)' : ''}</span>
              </div>
            ))}
            {unalloc > 0 && (
              <div style={{ fontSize: 11, color: C.grey, marginTop: 4 }}>{unalloc}% of the {comp} purse had no finisher to pay (fewer players than places) and came back to everyone equally.</div>
            )}
          </div>
        );
      }))}

      {!resultsOnly && (
        <>
          <div style={{ fontSize: 12, color: C.grey, fontWeight: 600, margin: '12px 0 6px' }}>Net winnings</div>
          {totals.map(t => (
            <div key={t.player_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', borderRadius: 8, background: '#f5f4ef', marginBottom: 4 }}>
              <span style={{ fontSize: 13 }}>{t.player_name}{t.prize_total != null ? <span style={{ color: C.grey, fontSize: 11 }}> — ${t.prize_total.toFixed(2)} prize, ${bet.toFixed(2)} buy-in</span> : null}</span>
              <Money value={t.net_amount || 0} />
            </div>
          ))}
          <div style={{ fontSize: 11, color: C.grey, marginTop: 4 }}>{n} players &times; ${bet.toFixed(2)} = ${(n * bet).toFixed(2)}. Each pair settles one line; everyone&rsquo;s total nets to zero.</div>
        </>
      )}

      {rows.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <CollapsibleScorecard title={showNet ? 'View net scorecard' : 'View scorecard'} collapsible={collapsible}>
            <ScoreGrid headerCells={{ totLabel: 'TOT' }} rows={rows} markFor={markFor} par={parArr} hcpIndex={hcpArr} />
          </CollapsibleScorecard>
          <div style={{ fontSize: 11, color: C.grey, margin: '6px 0 0' }}>{showNet ? 'Net shown — gross minus playing-handicap strokes. PH = playing handicap.' : 'Gross shown.'}</div>
        </div>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * BEST BALL block (Sept 23, 2026 — Stroke_Play_Teams_Design_v1): team placings
 * per competition (each team's own line, its members' own nets underneath, the
 * prize EACH player collects), teams the game could not rank, net winnings per
 * player, and a best-ball card — each team's counted score per hole with its
 * members' nets beneath, the scores that counted circled.
 * ------------------------------------------------------------------------- */
function BestBallBlock({ game, collapsible }) {
  const res = game.results || {};
  const totals = res.totals || [];
  const playersRes = res.players || [];
  const holes = res.holes || [];
  const info = game.team_info || {};
  const bet = parseFloat(game.bet_amount || 0);
  const competition = res.competition || 'net';
  const comps = competition === 'both' ? ['net', 'gross'] : [competition];
  const resultsOnly = res.results_only !== false;
  const showNet = competition !== 'gross';
  const bestOf = res.best_of || info.best_of || 1;
  const inMoney = playersRes.filter(p => p.in_money);
  const counts = bestOf === 1 ? 'its best score' : `its best ${bestOf} scores`;

  // Per-player per-hole detail from the engine (playing-handicap strokes).
  const byPlayerHole = {};
  const parArr = Array(18).fill(null); const hcpArr = Array(18).fill(null);
  for (const h of holes) {
    const hi = h.hole_number - 1;
    if (hi >= 0 && hi < 18) { if (h.par != null) parArr[hi] = h.par; if (h.handicap_index != null) hcpArr[hi] = h.handicap_index; }
    for (const ns of (h.net_scores || [])) {
      (byPlayerHole[ns.player_id] = byPlayerHole[ns.player_id] || {})[h.hole_number] = { net: ns.net, gross: ns.gross, strokes: ns.strokes_received || 0 };
    }
  }
  const rows = [];
  for (const t of totals.filter(x => x.ranked !== false)) {
    const hc = t.holes_counted || {};
    const cells = Array.from({ length: 18 }, (_, i) => (hc[i + 1] ? (showNet ? hc[i + 1].net : hc[i + 1].gross) : null));
    const out = sum(cells.slice(0, 9)); const in_ = sum(cells.slice(9));
    rows.push({ name: t.player_name, sub: t.net_place_label || t.gross_place_label || null, cells, out, in_, tot: (out != null || in_ != null) ? (out || 0) + (in_ || 0) : null, bold: true, _team: t });
    for (const m of (t.members || [])) {
      const mc = Array.from({ length: 18 }, (_, i) => { const e = byPlayerHole[m.player_id]?.[i + 1]; return e ? (showNet ? e.net : e.gross) : null; });
      const mo = sum(mc.slice(0, 9)); const mi = sum(mc.slice(9));
      rows.push({ name: `\u00a0\u00a0${m.player_name}`, sub: showNet && m.playing_handicap != null ? `PH ${m.playing_handicap}` : null, cells: mc, out: mo, in_: mi, tot: (mo != null || mi != null) ? (mo || 0) + (mi || 0) : null, _pid: m.player_id, _counted: hc });
    }
    // The blind slot (Sept 26, 2026): one row per player in the chain — a withdrawn
    // player's holes, then the blind's — showing only the holes each one filled.
    for (const c of (t.blind && Array.isArray(t.blind.chain) ? t.blind.chain : [])) {
      const cc = Array.from({ length: 18 }, (_, i) => { const e = c.cells && c.cells[i + 1]; return e ? (showNet ? e.net : e.gross) : null; });
      const co = sum(cc.slice(0, 9)); const ci = sum(cc.slice(9));
      rows.push({ name: `\u00a0\u00a0${c.role === 'blind' ? '+ ' : ''}${c.player_name}`, sub: c.role === 'blind' ? `blind${c.band ? ` \u00b7 ${c.band}` : ''}${showNet && c.playing_handicap != null ? ` \u00b7 PH ${c.playing_handicap}` : ''}` : 'withdrew', cells: cc, out: co, in_: ci, tot: (co != null || ci != null) ? (co || 0) + (ci || 0) : null, _pid: c.player_id, _counted: hc, _slotCells: c.cells || {} });
    }
  }
  const markFor = (r, idx) => {
    if (!showNet || !r._pid) return null;
    const s = r._slotCells ? (r._slotCells[idx + 1]?.strokes || 0) : (byPlayerHole[r._pid]?.[idx + 1]?.strokes || 0);
    return s > 0 ? <sup style={{ fontSize: 7, color: '#16a34a', fontWeight: 700 }}>{'*'.repeat(s)}</sup> : null;
  };
  const cellCircle = (r, idx) => {
    if (!r._pid || !r._counted) return false;
    const c = r._counted[idx + 1];
    if (r._slotCells) return !!c && c.slot_id === r._pid && !!(showNet ? c.slot_counted_net : c.slot_counted_gross);
    return !!c && (showNet ? c.net_ids : c.gross_ids).includes(r._pid);
  };
  const how = res.tie_break === 'usga' ? 'the USGA scorecard playoff on the team\u2019s hole-by-hole net (last 9, then 6, 3 and the last hole)'
    : res.tie_break === 'hole_handicap' ? 'a match of cards, hardest hole first'
    : res.tie_break === 'committee' ? 'the organizer' : null;
  // "1–9", "10, 12, 14–18"
  const holeRange = (hs) => {
    const a = [...(hs || [])].sort((x, y) => x - y); const out = []; let i = 0;
    while (i < a.length) { let j = i; while (j + 1 < a.length && a[j + 1] === a[j] + 1) j++; out.push(j > i ? `${a[i]}\u2013${a[j]}` : `${a[i]}`); i = j + 1; }
    return out.join(', ') || 'none';
  };
  const memberLine = (t) => (t.members || []).map(m => `${m.player_name.split(' ').slice(-1)[0]} ${showNet ? (m.net_total ?? '\u2014') : (m.gross_total ?? '\u2014')}${showNet && m.playing_handicap != null ? ` (PH ${m.playing_handicap})` : ''}`)
    .concat((t.blind && Array.isArray(t.blind.chain) ? t.blind.chain : []).map(c => c.role === 'blind'
      ? `+ blind ${c.player_name}${c.band ? ` (${c.band} band)` : ''}, holes ${holeRange(c.holes_filled)}`
      : `${c.player_name} withdrew, holes ${holeRange(c.holes_filled)} stand`))
    .concat(t.blind && t.blind.hidden ? ['+ blind'] : [])
    .join(' \u00b7 ');
  const blindsInPlay = totals.some(t => t.includes_blind);
  const blindShares = (totals.find(t => t.blind) || {}).blind?.shares !== false;

  return (
    <section style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: C.grey, letterSpacing: '0.04em' }}>
        {/* Shamble's format label already says "shamble" — don't say it twice. */}
        {game.game_type === 'shamble' && info.format_label ? info.format_label.toUpperCase() : <>{(game.display_name || 'Best Ball').toUpperCase()}{info.format_label ? ` \u00b7 ${info.format_label.toUpperCase()}` : ''}</>} &middot; {resultsOnly ? 'placings only' : `$${bet.toFixed(2)} buy-in`}
      </div>
      <div style={{ fontSize: 17, fontWeight: 600, margin: '2px 0' }}>{`${game.game_type === 'shamble' ? 'Shamble' : 'Best ball'} ${resultsOnly ? 'results' : 'settlement'}`}</div>

      <div style={{ padding: '8px 10px', borderLeft: `4px solid ${C.green}`, background: C.netBg, border: `1px solid ${C.netBorder}`, borderLeftWidth: 4, fontSize: 11.5, color: '#065f46', margin: '8px 0 10px', lineHeight: 1.45 }}>
        <span>Each team counts {counts} on each hole. </span>
        {showNet && (
          <span><strong style={{ fontWeight: 700 }}>Net — {res.allowance_pct}% playing handicap, no team handicap.</strong> Each player&rsquo;s course handicap times {res.allowance_pct}%, rounded once; strokes on the hardest holes of his own tee (<sup style={{ fontSize: 8, color: '#16a34a', fontWeight: 700 }}>*</sup> marks a stroke hole). </span>
        )}
        {competition === 'gross' && <span><strong style={{ fontWeight: 700 }}>Gross.</strong> No handicap strokes. </span>}
        {competition === 'both' && <span>Gross is ranked as well — a team may place in both. </span>}
        {!resultsOnly && (
          <span>
            {res.segments ? segmentPayText(res) : <>
            {res.payout_mode === 'winner' ? 'Winner takes all' : `Paid places ${(res.places_pct || []).map(p => `${p}%`).join(' / ')}`}
            {competition === 'both' && res.purses ? ` — $${(res.purses.net.purse || 0).toFixed(2)} net purse, $${(res.purses.gross.purse || 0).toFixed(2)} gross purse` : res.purses ? ` of the $${((res.purses[competition] || {}).purse || 0).toFixed(2)} purse` : ''}.</>}
            {res.money_ties === 'split' ? ' Tied teams share the money for the places they occupy.' : ' Each decided place is paid its own share.'} Teammates split their team&rsquo;s winnings evenly.
          </span>
        )}
        {how && <span> Ties decided by {how}; SP = decided on the cards, PO = by playoff or the organizer.</span>}
        {blindsInPlay && (
          <span> <strong style={{ fontWeight: 700 }}>Blind draw.</strong> A 4-man team one player short played with a blind: a player drawn at random from the missing player&rsquo;s handicap band, whose scores filled the empty spot (a withdrawn player&rsquo;s holes stand; the blind covers the rest).{!resultsOnly && (blindShares ? ' The blind takes a player\u2019s share of that team\u2019s winnings, on top of his own team\u2019s.' : ' The blind was not paid; the team\u2019s three players split its share.')}</span>
        )}
      </div>
      {game.nine_hole_estimated && (
        <div style={{ fontSize: 11, color: C.grey, margin: '0 0 8px' }}>Strokes use an <strong style={{ fontWeight: 700 }}>est</strong>imated 9-hole handicap &mdash; see the note at the foot of this statement.</div>
      )}

      {statementParts(res).flatMap(part => comps.map(comp => {
        const ranked = part.totals.filter(t => t[`${comp}_place`] != null).sort((a, b) => a[`${comp}_place`] - b[`${comp}_place`]);
        const purse = part.purses && part.purses[comp] ? part.purses[comp] : null;
        const unalloc = purse && purse.unallocated_pct > 0 ? purse.unallocated_pct : 0;
        return (
          <div key={`${part.key}-${comp}`}>
            <div style={{ fontSize: 12, color: part.label ? C.greenText : C.grey, fontWeight: 600, margin: '12px 0 6px' }}>
              {part.label ? `${part.label} — ` : ''}{comps.length > 1 ? (comp === 'net' ? 'Net placings' : 'Gross placings') : 'Placings'}
            </div>
            {ranked.map(t => {
              const prize = t[`prize_${comp}`];
              const mark = t[`${comp}_tie_mark`];
              const pending = t[`${comp}_tie_status`] === 'pending';
              const score = comp === 'net'
                ? `${t.net_total ?? '\u2014'} net${t.net_vs_par != null ? ` (${fmtVs(t.net_vs_par)})` : ''}`
                : `${t.gross_total ?? '\u2014'} gross${t.gross_vs_par != null ? ` (${fmtVs(t.gross_vs_par)})` : ''}`;
              return (
                <div key={`${comp}-${t.side_id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 10px', borderRadius: 8, background: '#f5f4ef', marginBottom: 5 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, minWidth: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: C.dark, minWidth: 30 }}>{t[`${comp}_place_label`]}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{t.player_name}{mark ? <span style={{ fontSize: 10, color: C.grey, marginLeft: 6 }}>{mark}</span> : null}{pending ? <span style={{ fontSize: 10, color: '#b45309', marginLeft: 6 }}>playoff pending</span> : null}{t.short ? <span style={{ fontSize: 10, color: '#b45309', marginLeft: 6 }}>short a player</span> : null}</div>
                      <div style={{ fontSize: 11, color: C.grey }}>{score}</div>
                      <div style={{ fontSize: 10.5, color: C.tertiary }}>{memberLine(t)}</div>
                    </div>
                  </div>
                  {prize != null ? (
                    <span style={{ fontWeight: 700, fontSize: 13, color: prize > 0 ? C.greenText : C.tertiary, whiteSpace: 'nowrap' }}>{prize > 0 ? `$${prize.toFixed(2)}${(t.members || []).length > 1 ? ' each' : ''}` : '\u2014'}</span>
                  ) : null}
                </div>
              );
            })}
            {unalloc > 0 && (
              <div style={{ fontSize: 11, color: C.grey, marginTop: 4 }}>{unalloc}% of the {comp} purse had no finisher to pay (fewer teams than places) and came back to everyone equally.</div>
            )}
          </div>
        );
      }))}
      {totals.filter(t => t.ranked === false).map(t => (
        <div key={`u-${t.side_id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 10px', borderRadius: 8, background: '#f9fafb', marginBottom: 5, color: C.grey, fontSize: 12 }}>
          <span>{t.player_name} — not ranked ({t.unranked_reason === 'oversize' ? 'too many players on the team' : t.unranked_reason === 'no_handicap' ? 'a player has no handicap' : 'short a player'}){resultsOnly ? '' : '; not in the money'}</span>
        </div>
      ))}

      {!resultsOnly && (
        <>
          <div style={{ fontSize: 12, color: C.grey, fontWeight: 600, margin: '12px 0 6px' }}>Net winnings</div>
          {inMoney.map(p => (
            <div key={p.player_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', borderRadius: 8, background: '#f5f4ef', marginBottom: 4 }}>
              <span style={{ fontSize: 13 }}>{p.player_name}<span style={{ color: C.grey, fontSize: 11 }}> — {p.team_name}{p.prize_total != null ? ` \u00b7 $${p.prize_total.toFixed(2)} prize, $${bet.toFixed(2)} buy-in` : ''}{(p.blind_for || []).filter(b => b.prize > 0).map(b => ` \u00b7 incl. $${b.prize.toFixed(2)} as the blind for Team ${b.team_number}`).join('')}</span></span>
              <Money value={p.net_amount || 0} />
            </div>
          ))}
          <div style={{ fontSize: 11, color: C.grey, marginTop: 4 }}>{inMoney.length} players &times; ${bet.toFixed(2)} = ${(inMoney.length * bet).toFixed(2)}. Each pair settles one line; teammates owe each other nothing; everyone&rsquo;s total nets to zero.</div>
        </>
      )}

      {rows.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <CollapsibleScorecard title={showNet ? 'View best-ball net card' : 'View best-ball card'} collapsible={collapsible}>
            <ScoreGrid headerCells={{ totLabel: 'TOT' }} rows={rows} markFor={markFor} cellCircle={cellCircle} par={parArr} hcpIndex={hcpArr} />
          </CollapsibleScorecard>
          <div style={{ fontSize: 11, color: C.grey, margin: '6px 0 0' }}>Bold rows are the team&rsquo;s counted score; each player&rsquo;s own {showNet ? 'net' : 'score'} is beneath, with the scores that counted circled.{showNet ? ' PH = playing handicap.' : ''}</div>
        </div>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * PUTTS block: participant-scoped putts scorecard + net winnings.
 * ------------------------------------------------------------------------- */
function PuttsBlock({ game, collapsible }) {
  const totals = game.results?.totals || [];
  const holes = game.results?.holes || [];
  const bet = parseFloat(game.bet_amount || 0);

  // Putts grid rows from per-hole putts if available.
  const playerIds = []; const nameById = {}; const puttByHole = {};
  for (const h of holes) for (const ns of (h.net_scores || [])) {
    if (!nameById[ns.player_id]) { nameById[ns.player_id] = ns.player_name; playerIds.push(ns.player_id); }
    puttByHole[ns.player_id] = puttByHole[ns.player_id] || {};
    puttByHole[ns.player_id][h.hole_number] = ns.putts;
  }
  const rows = playerIds.map(pid => {
    const cells = Array.from({ length: 18 }, (_, i) => puttByHole[pid]?.[i + 1] ?? null);
    const out = sum(cells.slice(0, 9)); const in_ = sum(cells.slice(9));
    const tot = (out != null || in_ != null) ? (out || 0) + (in_ || 0) : null;
    return { name: nameById[pid], cells, out, in_, tot };
  });

  const leader = totals.length ? Math.min(...totals.filter(t => t.total_putts != null).map(t => t.total_putts)) : null;

  return (
    <section style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: C.grey, letterSpacing: '0.04em' }}>
        {(game.display_name || 'Putts').toUpperCase()} &middot; ${bet.toFixed(2)} / putt
      </div>
      <div style={{ fontSize: 17, fontWeight: 600, margin: '2px 0' }}>Putts settlement</div>

      {rows.length > 0 && (
        <CollapsibleScorecard title="View putts scorecard" collapsible={collapsible}>
          <ScoreGrid headerCells={{ totLabel: 'PUT' }} rows={rows} />
        </CollapsibleScorecard>
      )}
      <div style={{ fontSize: 11, color: C.grey, margin: '6px 0 10px' }}>Putts grid only — no strokes applied. Lowest total wins.</div>

      <div style={{ fontSize: 12, color: C.grey, fontWeight: 600, margin: '12px 0 6px' }}>Net winnings</div>
      {totals.map(t => (
        <div key={t.player_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', borderRadius: 8, background: '#f5f4ef', marginBottom: 4 }}>
          <span style={{ fontSize: 13 }}>{t.player_name} — {t.total_putts} putts{t.total_putts === leader ? ' (leader)' : ''}</span>
          <Money value={t.net_amount || 0} />
        </div>
      ))}
    </section>
  );
}
